import { useEffect, useState, useRef, useCallback } from "react";
import { useMagnifierStore } from "@/stores/magnifier-store";
import { toast } from "sonner";

/**
 * SmartMagnifier — a real magnifying lens.
 *
 * Panels hold iframes (PDF/Slides/browser), canvases (whiteboard) and videos, whose
 * pixels cannot be read from the page. So the lens magnifies the *captured tab*
 * instead: a hidden <video> fed by getDisplayMedia is cropped around the cursor and
 * drawn, scaled up, into the lens canvas. That magnifies anything on screen —
 * terminal commands, slides, PDF text, the embedded browser.
 *
 * The lens is drawn offset from the cursor: the crop is taken at the cursor, so an
 * overlapping lens would film itself and produce an infinite-mirror artifact.
 */
export default function SmartMagnifier({ containerRef, recordingStream = null }) {
  const {
    isEnabled,
    mode,
    size,
    scale,
    shape,
    borderColor,
    borderWidth,
    opacity,
    duration,
    frozen,
    halo,
    zoomTarget,
    toggleEnabled,
    setMode,
    setPosition,
    setZoomTarget,
    setZoomProgress,
    setFrozen,
    updateSettings,
  } = useMagnifierStore();

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [drawEnd, setDrawEnd] = useState({ x: 0, y: 0 });
  const [captureReady, setCaptureReady] = useState(false);
  const animationRef = useRef(null);

  // Lens plumbing
  const videoRef = useRef(null);
  const lensRef = useRef(null);
  const streamRef = useRef(null);
  const ownsStreamRef = useRef(false);
  const rafRef = useRef(null);
  const cursorRef = useRef({ x: 0, y: 0 });

  const lensW = size;
  const lensH = shape === "circle" ? size : Math.round(size * 0.6);
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  // ── Capture source ──
  const attachStream = useCallback(async (stream) => {
    streamRef.current = stream;
    const v = videoRef.current;
    if (!v) return;
    v.srcObject = stream;
    try {
      await v.play();
    } catch {}
    setCaptureReady(true);
  }, []);

  const releaseStream = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current && ownsStreamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    ownsStreamRef.current = false;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCaptureReady(false);
  }, []);

  useEffect(() => {
    if (!isEnabled) return;
    let cancelled = false;

    (async () => {
      // Reuse the recording capture when one exists — avoids a 2nd permission prompt
      const live = recordingStream?.getVideoTracks()[0]?.readyState === "live";
      if (live) {
        ownsStreamRef.current = false;
        await attachStream(recordingStream);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 30 },
          audio: false,
          preferCurrentTab: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        ownsStreamRef.current = true;
        await attachStream(stream);
        stream.getVideoTracks()[0]?.addEventListener("ended", () => {
          useMagnifierStore.getState().setEnabled(false);
        });
      } catch {
        toast.error("Loupe : partage de l'onglet refusé");
        useMagnifierStore.getState().setEnabled(false);
      }
    })();

    return () => {
      cancelled = true;
      releaseStream();
    };
  }, [isEnabled, recordingStream, attachStream, releaseStream]);

  // ── Track cursor (client coords) ──
  useEffect(() => {
    if (!isEnabled) return;
    const onMove = (e) => {
      if (frozen) return;
      cursorRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [isEnabled, frozen]);

  // ── Lens draw loop ──
  useEffect(() => {
    if (!isEnabled || !captureReady || zoomTarget) return;

    const draw = () => {
      const v = videoRef.current;
      const c = lensRef.current;
      if (v && c && v.videoWidth > 0) {
        const { x, y } = cursorRef.current;
        const bounds = containerRef.current?.getBoundingClientRect();
        const inside =
          !bounds ||
          (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom);

        if (!inside) {
          c.style.display = "none";
        } else {
          c.style.display = "block";
          const ctx = c.getContext("2d");
          // Tab capture frames map 1:1 onto the viewport
          const rx = v.videoWidth / window.innerWidth;
          const ry = v.videoHeight / window.innerHeight;
          const sw = (lensW / scale) * rx;
          const sh = (lensH / scale) * ry;

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.clearRect(0, 0, c.width, c.height);
          ctx.drawImage(v, x * rx - sw / 2, y * ry - sh / 2, sw, sh, 0, 0, c.width, c.height);

          // Keep the lens clear of the crop area, or it films itself
          const gap = Math.max(28, lensW / scale / 2 + 16);
          let lx = x + gap;
          let ly = y - lensH - gap;
          if (lx + lensW > window.innerWidth - 8) lx = x - lensW - gap;
          if (ly < 8) ly = y + gap;
          if (ly + lensH > window.innerHeight - 8) ly = window.innerHeight - lensH - 8;
          c.style.left = `${Math.max(8, lx)}px`;
          c.style.top = `${Math.max(8, ly)}px`;
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isEnabled, captureReady, zoomTarget, scale, lensW, lensH, containerRef]);

  // ── Zoom animation (Alt+Click on a word scales the whole grid toward it) ──
  const animateZoom = useCallback(
    (target) => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      const startProgress = useMagnifierStore.getState().zoomProgress;
      const endProgress = target ? 1 : 0;
      const startTime = performance.now();

      if (target) {
        setZoomTarget(target);
        setPosition({ x: target.x, y: target.y });
      }

      const animate = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        const ease = 1 - Math.pow(1 - t, 3);
        setZoomProgress(startProgress + (endProgress - startProgress) * ease);
        if (t < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else if (!target) {
          setZoomTarget(null);
        }
      };
      animationRef.current = requestAnimationFrame(animate);
    },
    [duration, setPosition, setZoomProgress, setZoomTarget]
  );

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        toggleEnabled();
        toast.info(isEnabled ? "Loupe désactivée" : "Loupe activée");
      }
      if (e.altKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setFrozen(!frozen);
        toast.info(frozen ? "Position dégelée" : "Position gelée");
      }
      if (e.altKey && e.key === "+") {
        e.preventDefault();
        updateSettings({ scale: Math.min(10.0, scale + 0.5) });
      }
      if (e.altKey && e.key === "-") {
        e.preventDefault();
        updateSettings({ scale: Math.max(1.2, scale - 0.5) });
      }
      if (e.key === "Escape") {
        if (zoomTarget) {
          e.preventDefault();
          animateZoom(null);
        } else if (isEnabled) {
          e.preventDefault();
          useMagnifierStore.getState().setEnabled(false);
          toast.info("Loupe désactivée");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEnabled, frozen, scale, zoomTarget, toggleEnabled, setFrozen, updateSettings, animateZoom]);

  // ── Alt+Click: zoom the grid onto the clicked word ──
  useEffect(() => {
    const handleMouseClick = (e) => {
      if (!e.altKey) return;
      e.preventDefault();

      let range = null;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.setEnd(pos.offsetNode, pos.offset);
        }
      }

      if (range) {
        try {
          range.expand("word");
          const word = range.toString().trim();
          const rect = range.getBoundingClientRect();
          const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };

          if (word.length > 0 && rect.width > 0) {
            animateZoom({
              x: rect.left - containerRect.left + rect.width / 2,
              y: rect.top - containerRect.top + rect.height / 2,
              w: rect.width + 20,
              h: rect.height + 12,
            });
            toast.success(`Zoom sur le mot : "${word}"`);
            return;
          }
        } catch (err) {}
      }

      const targetEl = e.target;
      if (targetEl && targetEl !== containerRef.current) {
        const rect = targetEl.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
        animateZoom({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
          w: rect.width + 30,
          h: rect.height + 20,
        });
        toast.info("Zoom sur l'élément sélectionné");
      }
    };

    const container = containerRef.current;
    if (container) container.addEventListener("click", handleMouseClick, true);
    return () => {
      if (container) container.removeEventListener("click", handleMouseClick, true);
    };
  }, [containerRef, animateZoom]);

  // ── Free-drawing zoom region ──
  useEffect(() => {
    if (mode !== "draw" || !isEnabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      if (e.button !== 0) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setIsDrawing(true);
      setDrawStart({ x, y });
      setDrawEnd({ x, y });
    };

    const handleMouseMove = (e) => {
      if (!isDrawing) return;
      const rect = container.getBoundingClientRect();
      setDrawEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const handleMouseUp = () => {
      if (!isDrawing) return;
      setIsDrawing(false);
      const x = Math.min(drawStart.x, drawEnd.x);
      const y = Math.min(drawStart.y, drawEnd.y);
      const w = Math.abs(drawStart.x - drawEnd.x);
      const h = Math.abs(drawStart.y - drawEnd.y);
      if (w > 15 && h > 15) {
        animateZoom({ x: x + w / 2, y: y + h / 2, w, h });
        toast.success("Zone libre sélectionnée");
      }
      setMode("cursor");
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [mode, isEnabled, isDrawing, drawStart, drawEnd, setMode, containerRef, animateZoom]);

  return (
    <>
      {/* Hidden capture source feeding the lens */}
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        aria-hidden="true"
        style={{ position: "fixed", left: -10000, top: 0, width: 2, height: 2, opacity: 0, pointerEvents: "none" }}
      />

      {/* The lens: a live, magnified crop of the screen under the cursor */}
      {isEnabled && captureReady && !zoomTarget && (
        <canvas
          ref={lensRef}
          width={Math.round(lensW * dpr)}
          height={Math.round(lensH * dpr)}
          className="pointer-events-none fixed z-[9995] select-none"
          style={{
            width: lensW,
            height: lensH,
            borderRadius: shape === "circle" ? "50%" : "12px",
            border: `${borderWidth}px solid ${borderColor}`,
            boxShadow: halo
              ? `0 0 22px ${borderColor}80, 0 12px 32px rgba(0,0,0,0.55)`
              : "0 12px 32px rgba(0,0,0,0.55)",
            opacity,
            background: "#0f0f1a",
          }}
        />
      )}

      {/* Free drawing selection rectangle */}
      {isDrawing && (
        <div
          className="absolute z-[9990] border-2 border-dashed pointer-events-none"
          style={{
            borderColor: borderColor,
            background: `${borderColor}15`,
            left: Math.min(drawStart.x, drawEnd.x),
            top: Math.min(drawStart.y, drawEnd.y),
            width: Math.abs(drawStart.x - drawEnd.x),
            height: Math.abs(drawStart.y - drawEnd.y),
            boxShadow: `0 0 10px ${borderColor}30`,
          }}
        />
      )}

    </>
  );
}
